<template>
  <div class="cfg-tab">
    <div class="cfg-chips" role="radiogroup" aria-label="Ce s-a modificat">
      <button
        v-for="f in filters"
        :key="f.key"
        type="button"
        role="radio"
        class="cfg-chip"
        :class="{ 'is-active': entity === f.key }"
        :aria-checked="entity === f.key"
        @click="entity = f.key"
      >
        <span class="cfg-chip__name">{{ f.label }}</span>
      </button>
    </div>

    <div class="cfg-toolbar">
      <h3 class="panel-title">Jurnalul modificărilor <span class="count">{{ filtered.length }}</span></h3>
      <span class="grow-spacer" />
      <v-text-field
        v-model="search"
        class="cfg-search"
        prepend-inner-icon="mdi-magnify"
        placeholder="Caută în jurnal"
        aria-label="Caută în jurnal"
        outlined
        dense
        single-line
        hide-details
        clearable
      ></v-text-field>
      <v-btn icon aria-label="Reîncarcă" title="Reîncarcă" :loading="loading" @click="reload">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <v-progress-linear v-if="loading && !loaded" indeterminate color="primary" class="mb-2" />
    <div class="cfg-list">
      <div v-if="loaded && !filtered.length" class="cfg-empty">Nicio modificare.</div>
      <div v-for="row in filtered" :key="row.id" class="cfg-row log__row">
        <div class="log__when">
          <span class="log__date">{{ formatDateTime(row.date) }}</span>
          <span class="log__user">{{ row.user || "—" }}</span>
        </div>
        <div class="cfg-row__main">
          <div class="cfg-row__meta log__tags">
            <span class="cfg-tag cfg-tag--sky">{{ entityLabel(row.entity) }}</span>
            <span class="cfg-tag" :class="actionTone(row.action)">{{ actionLabel(row.action) }}</span>
          </div>
          <div class="log__summary">{{ row.summary }}</div>
        </div>
      </div>
    </div>
    <div v-if="canLoadMore" class="log__more">
      <v-btn text color="primary" :loading="loading" @click="loadMore">
        <v-icon left>mdi-chevron-down</v-icon>Încarcă mai multe
      </v-btn>
    </div>
  </div>
</template>

<script>
import * as api from "./api";
import configMixin from "./configMixin";
import { formatDateTime, plain } from "./format";

const ENTITIES = {
  "equipment-type": "Tip echipament",
  cause: "Cauză",
  circuit: "Circuit",
  "catalog-kind": "Catalog: categorie",
  "catalog-model": "Catalog: model",
  department: "Departament",
  location: "Locație",
};
// rows that are not a Configurare change (the marker left by an update of the application); no filter chip
const OTHER_ENTITIES = {
  system: "Sistem",
};
const ACTIONS = {
  create: ["Adăugat", "cfg-tag--green"],
  update: ["Modificat", "cfg-tag--amber"],
  delete: ["Șters", "cfg-tag--brick"],
  replace: ["Versiune nouă", "cfg-tag--plum"],
  migrate: ["Actualizare", "cfg-tag--teal"],
};
const PAGE = 300;
const MAX = 2000;

export default {
  name: "LogTab",
  mixins: [configMixin],
  data() {
    return {
      rows: [],
      entity: "",
      search: "",
      limit: PAGE,
      filters: [{ key: "", label: "Toate" }, ...Object.entries(ENTITIES).map(([key, label]) => ({ key, label }))],
    };
  },
  computed: {
    filtered() {
      const term = plain(this.search);
      if (!term) return this.rows;
      return this.rows.filter((r) => plain(`${r.summary || ""} ${r.user || ""} ${this.entityLabel(r.entity)}`).includes(term));
    },
    // the server returned a full page, so there may be older rows
    canLoadMore() {
      return this.rows.length >= this.limit && this.limit < MAX;
    },
  },
  watch: {
    entity() {
      this.limit = PAGE;
      this.reload();
    },
  },
  methods: {
    formatDateTime,
    async load() {
      this.rows = await api.getLog(this.entity, this.limit);
    },
    loadMore() {
      this.limit = Math.min(this.limit + PAGE * 2, MAX);
      this.reload();
    },
    entityLabel(entity) {
      return ENTITIES[entity] || OTHER_ENTITIES[entity] || entity || "—";
    },
    actionLabel(action) {
      return (ACTIONS[action] || [action])[0];
    },
    actionTone(action) {
      return (ACTIONS[action] || [])[1] || "";
    },
  },
};
</script>

<style scoped>
.cfg-row.log__row {
  gap: 6px 18px;
}
.log__when {
  flex: 0 0 170px;
  display: flex;
  flex-direction: column;
}
.log__date {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.log__user {
  color: var(--ink-3);
  font-size: 0.92rem;
}
.log__tags {
  margin-top: 0;
}
.log__summary {
  margin-top: 6px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}
.log__more {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}
@media (max-width: 600px) {
  .log__when {
    flex: 1 1 100%;
    flex-direction: row;
    gap: 10px;
  }
}
</style>
